import { useState } from 'react'
import { Link as RouterLink } from 'react-router'
import dayjs from 'dayjs'
import { useDebounce } from 'usehooks-ts'

import LoginOutlinedIcon from '@ant-design/icons/LoginOutlined'
import SearchOutlinedIcon from '@ant-design/icons/SearchOutlined'

import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import useMediaQuery from '@mui/material/useMediaQuery'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import { useMe } from '@shared/providers/src/MeProvider'
import { UserRole, userRoleToLabel } from '@shared/utils'

import { useLoginAs } from '@pages/Users/User/User.hooks'
import Avatar from '@components/Avatar'
import { useUsers } from '@components/SelectUser/SelectUser.hooks'
import StatusChip from '@components/StatusChip'
import TableFilters, { FilterType } from '@components/TableFilters'
import Tip from '@components/Tip'

export default function Search({ size }) {
  const [open, setOpen] = useState(false)
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const handleClose = () => setOpen(false)

  return (
    <>
      <IconButton
        id="search-button"
        color="secondary"
        size={size}
        onClick={() => setOpen(true)}
        aria-label="Search"
        data-testid="header-search"
      >
        <SearchOutlinedIcon style={{ fontSize: 18 }} />
      </IconButton>
      <Dialog
        open={open}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        onClose={handleClose}
        scroll={fullScreen ? 'paper' : 'body'}
        slotProps={fullScreen ? undefined : { paper: { sx: { verticalAlign: 'top' } } }}
      >
        <DialogTitle onClose={handleClose}>Search</DialogTitle>
        <DialogContent>
          <Content onClose={handleClose} isMobile={fullScreen} />
        </DialogContent>
      </Dialog>
    </>
  )
}

function Content({ onClose, isMobile }) {
  const me = useMe()
  const isAdmin = me.role === UserRole.Admin

  const [value, setValue] = useState('')
  const [filters, setFilters] = useState({})
  const search = useDebounce(value.trim(), 500)

  const dob = filters.dob
  const taskId = filters.taskId?.trim()
  const enabled = search.length > 0 || Boolean(dob) || Boolean(taskId)
  const usersEnabled = isAdmin && search.length > 0

  const { data: patients, isPending: isPatientsLoading } = useUsers(
    { search, role: UserRole.Patient, dob, taskId, limit: 5 },
    {
      enabled,
      staleTime: 60 * 1000,
    }
  )

  const { data: users, isPending: isUsersLoading } = useUsers(
    { search, limit: 5 },
    {
      enabled: usersEnabled,
      staleTime: 60 * 1000,
    }
  )

  const showLoading = (isPatientsLoading || (usersEnabled && isUsersLoading)) && enabled
  const showEmpty = !showLoading && patients?.length === 0 && (!usersEnabled || users?.length === 0)
  const showPatients = !showLoading && patients?.length > 0
  const showUsers = usersEnabled && !showLoading && users?.length > 0

  return (
    <Stack sx={{ gap: 3 }}>
      <Tip>
        {isAdmin
          ? 'Search for a patient or user by their name, email, or MRN number.'
          : 'Search for a patient by their name, email, or MRN number.'}
      </Tip>
      <Stack>
        <TableFilters
          schema={[
            { key: 'dob', label: 'DOB', type: FilterType.DATE },
            { key: 'taskId', label: 'Task ID', type: FilterType.TEXT, placeholder: 'Search by Task ID' },
          ]}
          values={filters}
          onChange={(change) => setFilters((prev) => ({ ...prev, ...change }))}
          showReset={Boolean(dob || taskId)}
          onReset={() => setFilters({})}
          sx={{ p: 1 }}
        />
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          placeholder={isAdmin ? 'Search for a patient or user' : 'Search for a patient'}
          autoComplete="off"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          slotProps={{
            htmlInput: { 'data-testid': `search-input` },
          }}
        />
      </Stack>
      {showLoading && (
        <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: 300 }}>
          <CircularProgress />
        </Stack>
      )}
      {showEmpty && (
        <Stack spacing={1} data-testid="patient-results-empty">
          <Typography align="center" sx={{ color: 'text.secondary' }}>
            {isAdmin
              ? "We couldn't find any patients or users that match. Try searching with a different term."
              : "We couldn't find any patients that match. Try searching with a different term."}
          </Typography>
        </Stack>
      )}
      {showPatients && (
        <Stack sx={{ gap: 1 }}>
          <Typography variant="h4">Patients</Typography>
          <List disablePadding data-testid="patient-results-list">
            {patients.map((patient, i) => (
              <PatientResult key={patient.id} patient={patient} divider={i !== patients.length - 1} onClose={onClose} isMobile={isMobile} />
            ))}
          </List>
        </Stack>
      )}
      {showUsers && (
        <Stack sx={{ gap: 1 }}>
          <Typography variant="h4">Users</Typography>
          <List disablePadding data-testid="user-results-list">
            {users.map((user, i) => (
              <UserResult key={user.id} user={user} divider={i !== users.length - 1} onClose={onClose} />
            ))}
          </List>
        </Stack>
      )}
    </Stack>
  )
}

function UserResult({ user, divider, onClose }) {
  const loginAs = useLoginAs(user.id, { onConfirm: onClose })

  return (
    <ListItem
      disablePadding
      divider={divider}
      secondaryAction={
        <Tooltip title="Login as User">
          <span>
            <IconButton disabled={user.status === 'disabled'} onClick={() => loginAs()}>
              <LoginOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      <ListItemButton component={RouterLink} to={`/app/users/${user.id}`} onClick={onClose} sx={{ gap: 1.5, pr: 7 }}>
        <ListItemAvatar sx={{ minWidth: 40 }}>
          <Avatar user={user} />
        </ListItemAvatar>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography>{user.fullName}</Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            {user.role ? `${userRoleToLabel[user.role]} • ID ${user.id}` : `ID ${user.id}`}
          </Typography>
        </Stack>
      </ListItemButton>
    </ListItem>
  )
}

function PatientResult({ patient, divider, onClose, isMobile }) {
  const cboLocation = [patient.cbo?.city, patient.cbo?.state].filter(Boolean).join(', ')

  return (
    <ListItemButton divider={divider} component={RouterLink} to={`/app/patients/${patient.id}`} onClick={onClose} sx={{ gap: 2 }}>
      <ListItemAvatar sx={{ minWidth: 40 }}>
        <Avatar user={patient} size={isMobile ? 'lg' : 'md'} />
      </ListItemAvatar>
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
          <Typography noWrap>{patient.fullName}</Typography>
          <StatusChip.User status={patient.status} />
        </Stack>
        <Typography sx={{ color: 'text.secondary' }}>
          MRN {patient.id}
          {patient.dob && ` • DOB ${dayjs(patient.dob).format('L')}`}
        </Typography>
        {isMobile && patient.cbo && (
          <Typography noWrap sx={{ color: 'text.secondary' }}>
            {patient.cbo.name}
          </Typography>
        )}
      </Stack>
      {!isMobile && patient.cbo && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', ml: 'auto' }}>
          <Stack sx={{ textAlign: 'right', minWidth: 0 }}>
            <Typography noWrap>{patient.cbo.name}</Typography>
            {cboLocation && (
              <Typography sx={{ color: 'text.secondary' }} noWrap>
                {cboLocation}
              </Typography>
            )}
          </Stack>
          <Avatar.CBO card data={patient.cbo} />
        </Stack>
      )}
    </ListItemButton>
  )
}
